import { api } from "_shared/_stores/api";
import { logSchemaFailure } from "_shared/_utilities/log-schema-failure";
import type Guid from "_shared/guids/guid";
import {
  NetworkConnectionNoteSchema,
  type NetworkConnectionNoteJSON,
} from "users/networking/connections/notes/_schemas/network-connection-note-schema";

type NetworkConnectionNoteArgs = { connectionId: Guid };

/**
 * The caller's private note on a network connection (#1209).
 *
 * The server answers only the caller's own note, so the cache needs no session in its key: a change
 * of principal clears the whole cache. `null` means the caller has written no note; the server
 * answers 204 for that, and for a note cleared by saving empty text.
 */
export const networkConnectionNoteApi = api.injectEndpoints({
  endpoints: (build) => ({
    getNetworkConnectionNote: build.query<NetworkConnectionNoteJSON | null, NetworkConnectionNoteArgs>({
      query: ({ connectionId }) => ({
        url: `users/network-connections/${connectionId.value}/note`,
      }),
      rawResponseSchema: NetworkConnectionNoteSchema.nullable(),
      onSchemaFailure: logSchemaFailure,
      providesTags: (result, error, { connectionId }) => [
        { type: "NetworkConnectionNote", id: connectionId.value },
      ],
    }),
    saveNetworkConnectionNote: build.mutation<
      NetworkConnectionNoteJSON | null,
      NetworkConnectionNoteArgs & { body: string }
    >({
      query: ({ connectionId, body }) => ({
        url: `users/network-connections/${connectionId.value}/note`,
        method: "PUT",
        body: { body },
      }),
      rawResponseSchema: NetworkConnectionNoteSchema.nullable(),
      onSchemaFailure: logSchemaFailure,
      // The answer is the whole note, so it is written into the cache rather than read again.
      async onQueryStarted({ connectionId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            networkConnectionNoteApi.util.upsertQueryData(
              "getNetworkConnectionNote",
              { connectionId },
              data ?? null,
            ),
          );
        } catch {
          // The caller reports the failure from the mutation's own result.
        }
      },
    }),
  }),
});

export const { useGetNetworkConnectionNoteQuery, useSaveNetworkConnectionNoteMutation } =
  networkConnectionNoteApi;
